import React, { useMemo } from 'react';
import { ArrowLeft, Inbox, Users } from 'lucide-react';
import type { WorkspaceFolder } from '../../utils/workspaceFolders';
import type { LibraryWorkspace } from '../../utils/workspaceLibrary';
import { getDescendantFolderIds, getWorkspaceCostsByFolder, getWorkspaceCountsByFolder } from '../../utils/folderTree';
import { pluralize } from '../../utils/workspaceLibrary';
import { FolderTree, type TreeDropHandlers, type TreeLeaf } from './FolderTree';

export type ProjectPanelContext =
    | { kind: 'project'; project: WorkspaceFolder }
    | { kind: 'unfiled' }
    | { kind: 'shared' };

export interface ProjectPanelProps {
    context: ProjectPanelContext;
    folders: WorkspaceFolder[];
    workspaces: LibraryWorkspace[];
    /** Selected folder: a folder id, the project id for the project itself, or null for the context root. */
    selectedId: string | null;
    onSelect: (id: string | null) => void;
    expandedIds: Set<string>;
    onToggleExpand: (id: string) => void;
    /** The workspace that is open right now (highlighted in the outline). */
    currentWorkspaceId?: string | null;
    onOpenWorkspace: (workspaceId: string) => void;
    onWorkspaceDragStart?: (e: React.DragEvent, workspace: LibraryWorkspace) => void;
    onBack: () => void;
    backLabel?: string;
    renderMenu?: (folder: WorkspaceFolder) => React.ReactNode;
    /** Actions for a workspace row (e.g. Rename); nothing is shown when it returns null. */
    renderWorkspaceMenu?: (workspace: LibraryWorkspace) => React.ReactNode;
    drop?: TreeDropHandlers;
    /** Rendered under the outline (e.g. a "New workspace" control). */
    children?: React.ReactNode;
}

/**
 * Project-scoped navigation: Back, the project's name, then one Finder-style
 * outline of its folders with the workspaces nested inside them. Shared by the
 * library page (inside a project) and by the canvas (inside a workspace).
 */
export const ProjectPanel: React.FC<ProjectPanelProps> = ({
    context, folders, workspaces, selectedId, onSelect, expandedIds, onToggleExpand, currentWorkspaceId, onOpenWorkspace,
    onWorkspaceDragStart, onBack, backLabel = 'All projects', renderMenu, renderWorkspaceMenu, drop, children,
}) => {
    const project = context.kind === 'project' ? context.project : null;
    const owned = useMemo(() => workspaces.filter(w => !w.isShared), [workspaces]);
    // A project of this person's holds their own workspaces; one shared with them holds the ones shared through it
    const inScope = useMemo(() => (project?.isShared ? workspaces.filter(w => w.isShared) : owned), [workspaces, owned, project?.isShared]);
    const counts = useMemo(() => getWorkspaceCountsByFolder(folders, inScope), [folders, inScope]);
    const costs = useMemo(() => getWorkspaceCostsByFolder(folders, inScope), [folders, inScope]);
    const byId = useMemo(() => new Map(workspaces.map(w => [w.id, w])), [workspaces]);

    // The outline's folders: the project itself at the top plus everything below it.
    const outlineFolders = useMemo(() => {
        if (!project) return [];
        const ids = getDescendantFolderIds(folders, project.id);
        return [project, ...folders.filter(f => ids.has(f.id))];
    }, [folders, project]);

    // Workspaces grouped under their folder (only those inside this context).
    const leaves = useMemo(() => {
        const map = new Map<string | null, TreeLeaf[]>();
        const toLeaf = (w: LibraryWorkspace): TreeLeaf => ({
            id: w.id, label: w.name, thumbnail: w.thumbnail, current: w.id === currentWorkspaceId, note: w.isLocal ? 'On this device' : undefined, cost: w.totalCost,
        });
        const push = (key: string | null, w: LibraryWorkspace) => {
            const list = map.get(key) || [];
            list.push(toLeaf(w));
            map.set(key, list);
        };
        if (project) {
            const inOutline = new Set(outlineFolders.map(f => f.id));
            for (const w of inScope) if (w.folderId && inOutline.has(w.folderId)) push(w.folderId, w);
        } else if (context.kind === 'shared') {
            for (const w of workspaces) if (w.isShared) push(null, w);
        } else {
            const known = new Set(folders.map(f => f.id));
            for (const w of owned) if (!w.folderId || !known.has(w.folderId)) push(null, w);
        }
        for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
        return map;
    }, [project, outlineFolders, owned, workspaces, folders, context.kind, currentWorkspaceId]);

    const total = project
        ? counts.get(project.id) || 0
        : Array.from(leaves.values()).reduce((n, l) => n + l.length, 0);
    const alwaysExpanded = useMemo(() => new Set(project ? [project.id] : []), [project]);
    const title = project ? project.name : context.kind === 'shared' ? 'Shared with me' : 'Not in a project';

    return (
        <nav aria-label={project ? `Project ${project.name}` : title} className="flex flex-col min-h-0">
            {/* A row like the ones under it: its arrow starts where their content does */}
            <button type="button" className="ns-lib-row mb-3 text-ink-soft" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">{backLabel}</span>
            </button>

            {/* The project itself is the outline's top row, so only the other contexts need a heading */}
            {!project && (
                <div className="flex items-start gap-2.5 px-2 mb-3 min-w-0">
                    <span className="w-4 h-4 grid place-items-center text-ink-soft mt-0.5" aria-hidden="true">
                        {context.kind === 'shared' ? <Users className="w-4 h-4" /> : <Inbox className="w-4 h-4" />}
                    </span>
                    <div className="min-w-0">
                        <h2 className="ns-t-heading break-words">{title}</h2>
                        <p className="ns-t-small mt-0.5">{pluralize(total, 'workspace')}</p>
                    </div>
                </div>
            )}
            {project && (
                <h2 className="sr-only">{project.name}: {pluralize(total, 'workspace')}{outlineFolders.length > 1 ? `, ${pluralize(outlineFolders.length - 1, 'folder')}` : ''}</h2>
            )}

            <FolderTree
                variant="outline"
                folders={outlineFolders}
                counts={counts}
                costs={costs}
                selectedId={selectedId}
                onSelect={onSelect}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                alwaysExpandedIds={alwaysExpanded}
                mode="nav"
                rootLabel={project ? undefined : title}
                rootCount={project ? undefined : total}
                rootCost={project ? undefined : Array.from(leaves.values()).flat().reduce((sum, leaf) => sum + (leaf.cost ?? 0), 0)}
                renderMenu={renderMenu}
                renderLeafMenu={renderWorkspaceMenu ? (leaf) => { const w = byId.get(leaf.id); return w ? renderWorkspaceMenu(w) : null; } : undefined}
                drop={drop}
                leaves={leaves}
                onSelectLeaf={(leaf) => onOpenWorkspace(leaf.id)}
                onLeafDragStart={onWorkspaceDragStart ? (e, leaf) => { const w = byId.get(leaf.id); if (w) onWorkspaceDragStart(e, w); } : undefined}
                ariaLabel={project ? `Contents of ${project.name}` : title}
            />
            {project && total === 0 && outlineFolders.length === 1 && (
                <p className="ns-t-small px-2 py-1.5">Nothing in this project yet.</p>
            )}

            {children}
        </nav>
    );
};
